import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

// The sign-in field, an abstract instrument (ported from Tradehaus's TradeField for now; Hauser's own motion and imagery
// come later). A grid of short hairline ticks whose weight drifts slowly across the field; every tick turns to face the
// cursor, like iron filings round a magnet, and with no cursor follows a slow current. Every few seconds a signal rolls
// out from somewhere in the field and the ticks it passes stand up, lengthen and flash lime. Signing in swings every
// tick toward the form, then the field fades. Monochrome except the signal. Decorative only.

const STEP = 22;
// dark ink ticks on the chalk ground (#BBB7AF); the signal stays lime
const SILK = '55, 55, 54';
const LIME = '192, 255, 2';

export interface AuthFieldRef {
  /** swing the ticks toward the form and fade; resolves when done */
  leave: () => Promise<void>;
}

type Cell = { x: number; y: number; boost: number };
type Wave = { x: number; y: number; t: number };

export const AuthField = forwardRef<AuthFieldRef>(function AuthField(_, ref) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const api = useRef<AuthFieldRef>({ leave: () => Promise.resolve() });
  useImperativeHandle(ref, () => ({ leave: () => api.current.leave() }), []);

  useEffect(() => {
    const cv = canvas.current;
    const ctx = cv?.getContext('2d');
    if (!cv || !ctx) return undefined;
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let W = 0;
    let H = 0;
    let cells: Cell[] = [];
    let waves: Wave[] = [];
    let raf = 0;
    let alive = true;
    let exit: number | null = null;
    let aim: { x: number; y: number } | null = null;
    let look: { x: number; y: number } | null = null;
    const t0 = performance.now();

    const build = () => {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      W = cv.clientWidth;
      H = cv.clientHeight;
      cv.width = W * dpr;
      cv.height = H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      cells = [];
      const ox = ((W % STEP) + STEP) / 2;
      const oy = ((H % STEP) + STEP) / 2;
      for (let y = oy; y < H; y += STEP) for (let x = ox; x < W; x += STEP) cells.push({ x, y, boost: 0 });
    };
    // a soft value field (two slow sines) for the ticks' weight and the resting current
    const field = (x: number, y: number, t: number) =>
      Math.sin(x * 0.006 + t * 0.15) * Math.cos(y * 0.007 - t * 0.12) + Math.sin((x + y) * 0.004 + t * 0.09) * 0.5;
    const signal = () => {
      waves.push({ x: W * (0.1 + Math.random() * 0.8), y: H * (0.08 + Math.random() * 0.6), t: 0 });
    };

    const frame = (now = performance.now()) => {
      const t = (now - t0) / 1000;
      if (aim) look = look ? { x: look.x + (aim.x - look.x) * 0.12, y: look.y + (aim.y - look.y) * 0.12 } : { ...aim };
      let fade = 1;
      let swing = 0;
      if (exit) {
        const e = Math.min(1, (now - exit) / 1000);
        swing = Math.min(1, e * 1.8);
        fade = 1 - Math.max(0, (e - 0.45) / 0.55);
      }
      ctx.clearRect(0, 0, W, H);
      ctx.lineCap = 'round';
      const hot: [number, number, number, number, number][] = [];
      waves.forEach((w) => {
        w.t += 1 / 60;
      });
      cells.forEach((c) => {
        const f = field(c.x, c.y, still ? 0 : t);
        // toward the cursor if there is one, else along the current; on exit, toward the form (to the right)
        let ang = look ? Math.atan2(look.y - c.y, look.x - c.x) : f * 1.6 + (still ? 0 : t * 0.05);
        if (swing) {
          const to = Math.atan2(H * 0.5 - c.y, W + 200 - c.x);
          let d = to - ang;
          d = Math.atan2(Math.sin(d), Math.cos(d));
          ang += d * swing;
        }
        const near = look ? Math.max(0, 1 - Math.hypot(look.x - c.x, look.y - c.y) / 260) : 0;
        let wave = 0;
        waves.forEach((w) => {
          const R = w.t * 520;
          const d = Math.hypot(w.x - c.x, w.y - c.y);
          const band = 1 - Math.abs(d - R) / 46;
          if (band > 0) wave = Math.max(wave, band * Math.max(0, 1 - w.t / 2.4));
        });
        c.boost += (wave - c.boost) * 0.25;
        const len = 3 + (f + 1.5) * 1.6 + near * 7 + c.boost * 9;
        const alpha = (0.1 + (f + 1.5) * 0.07 + near * 0.5) * fade;
        const dx = (Math.cos(ang) * len) / 2;
        const dy = (Math.sin(ang) * len) / 2;
        if (c.boost > 0.08) hot.push([c.x - dx, c.y - dy, c.x + dx, c.y + dy, c.boost * fade]);
        else {
          ctx.globalAlpha = Math.min(0.9, alpha);
          ctx.strokeStyle = `rgb(${SILK})`;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(c.x - dx, c.y - dy);
          ctx.lineTo(c.x + dx, c.y + dy);
          ctx.stroke();
        }
      });
      ctx.lineWidth = 1.4;
      hot.forEach(([x1, y1, x2, y2, b]) => {
        ctx.globalAlpha = Math.min(1, 0.25 + b * 0.85);
        ctx.strokeStyle = `rgb(${LIME})`;
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
      });
      ctx.globalAlpha = 1;
      waves = waves.filter((w) => w.t < 2.6);
      if (alive && (!still || exit) && !(exit && fade <= 0)) raf = requestAnimationFrame(frame);
    };

    build();
    frame();
    const tick = still ? 0 : window.setInterval(signal, 3800);
    const first = still ? 0 : window.setTimeout(signal, 900);
    api.current.leave = () =>
      new Promise<void>((resolve) => {
        exit = performance.now();
        aim = null;
        look = null;
        if (still) {
          resolve();
          return;
        }
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(frame);
        window.setTimeout(resolve, 1000);
      });
    const ro = new ResizeObserver(() => {
      build();
      if (still) frame();
    });
    ro.observe(cv);
    const host = (cv.closest('[data-field-host]') as HTMLElement | null) ?? cv.parentElement ?? cv;
    const move = (e: PointerEvent) => {
      const r = cv.getBoundingClientRect();
      aim = { x: e.clientX - r.left, y: e.clientY - r.top };
      if (still) {
        look = aim;
        frame();
      }
    };
    const reset = () => {
      aim = null;
      look = null;
      if (still) frame();
    };
    host.addEventListener('pointermove', move);
    host.addEventListener('pointerleave', reset);
    return () => {
      alive = false;
      window.clearInterval(tick);
      window.clearTimeout(first);
      cancelAnimationFrame(raf);
      ro.disconnect();
      host.removeEventListener('pointermove', move);
      host.removeEventListener('pointerleave', reset);
    };
  }, []);

  return <canvas ref={canvas} aria-hidden style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block' }} />;
});

export default AuthField;
